/**
 * VÉRAULT Member Suite · Family-facing Packing (Wave 31 · Track C · Phase 2)
 * ============================================================================
 * The family's view of the advisor-curated packing list, plus their own
 * private personal items.
 *
 * STRICT RULES:
 *  · No mention of AI / Claude / LLM / model / generated anywhere.
 *  · When the trip is a surprise, category names + summary are server-
 *    sanitised to spoiler-safe labels.  We don't display any extra
 *    location metadata here.
 *  · Master list items are advisor-recommended; the family can:
 *      - check / uncheck packed
 *      - dismiss from their own view
 *  · Personal list items are private to this logged-in traveler:
 *      - add / edit / quantity / notes / packed / delete.
 *
 * UX
 *  · Empty state (not published): warm note + advisor's name.
 *  · Published: editorial summary, packed counter, category cards with
 *    the family's own check state, personal additions block, add forms.
 */
import React from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Check,
  ChevronDown,
  Plus,
  Trash2,
  X,
  PackageOpen,
  Sparkles,
  ExternalLink,
} from "lucide-react";
import {
  api,
  Surface,
  PageHeader,
  EditorialRule,
  motion as portalMotion,
} from "../../index";

const { fadeInUp, stagger } = portalMotion;

export default function ClientPackingPage() {
  const { tripId } = useParams();
  const [data, setData] = React.useState(null);
  const [busy, setBusy] = React.useState(false);

  const load = React.useCallback(() => {
    api.get(`/client-portal/trips/${tripId}/packing`)
      .then(({ data }) => setData(data))
      .catch(() => setData({ published: false }));
  }, [tripId]);

  React.useEffect(() => { load(); }, [load]);

  const canCheck = data?.permissions?.can_check_items !== false;
  const canEditPersonal = data?.permissions?.can_edit_personal !== false;

  const toggleMaster = async (itemId, packed) => {
    if (!canCheck) return;
    await api.post(`/client-portal/trips/${tripId}/packing/state/toggle`,
      { item_id: itemId, packed });
    load();
  };
  const hideMaster = async (itemId) => {
    if (!window.confirm("Remove this item from your packing list?")) return;
    await api.post(`/client-portal/trips/${tripId}/packing/state/hide`,
      { item_id: itemId });
    load();
  };
  const togglePersonal = async (itemId, packed) => {
    await api.patch(`/client-portal/trips/${tripId}/packing/personal/${itemId}`,
      { packed });
    load();
  };
  const renamePersonal = async (itemId, name) => {
    await api.patch(`/client-portal/trips/${tripId}/packing/personal/${itemId}`,
      { name });
    load();
  };
  const deletePersonal = async (itemId) => {
    await api.delete(`/client-portal/trips/${tripId}/packing/personal/${itemId}`);
    load();
  };
  const addPersonal = async (payload) => {
    setBusy(true);
    try {
      await api.post(`/client-portal/trips/${tripId}/packing/personal`, payload);
      load();
    } finally {
      setBusy(false);
    }
  };

  if (data === null) {
    return (
      <div className="px-6 py-10">
        <div className="h-12 mb-4 rounded bg-[hsl(var(--background-paper))]/40 animate-pulse" />
        <div className="h-32 rounded bg-[hsl(var(--background-paper))]/40 animate-pulse" />
      </div>
    );
  }

  if (!data.published) {
    return <EmptyState advisorName={data.advisor_name || "your advisor"} />;
  }

  const sharedTotal = (data.shared?.categories || [])
    .reduce((acc, c) => acc + (c.items?.length || 0), 0);
  const sharedPacked = (data.shared?.categories || [])
    .reduce((acc, c) => acc + (c.items || []).filter((i) => i.packed).length, 0);
  const personalTotal = (data.personal?.items || []).length;
  const personalPacked = (data.personal?.items || []).filter((i) => i.packed).length;
  const categoryCount = data.category_count || (data.shared?.categories || []).length;

  return (
    <motion.div
      initial="initial"
      animate="animate"
      variants={stagger}
      className="space-y-8 px-4 md:px-8 py-8"
      data-testid="client-packing-page"
    >
      <motion.div variants={fadeInUp}>
        <PageHeader
          eyebrow="Member Suite · Packing"
          title="A thoughtful list for your upcoming journey."
          lede={
            data.summary ||
            `${data.advisor_name || "Antonia"} curated a thoughtful packing list for your upcoming journey. It's organized by category so you can prepare with ease.`
          }
        />
      </motion.div>

      <motion.div variants={fadeInUp}>
        <div
          className="flex flex-wrap items-center gap-3 font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))]"
          data-testid="client-packing-progress"
        >
          <span>{sharedPacked + personalPacked} / {sharedTotal + personalTotal} packed</span>
          <span>·</span>
          <span>
            Organised into {categoryCount} thoughtful {categoryCount === 1 ? "category" : "categories"}.
          </span>
        </div>
      </motion.div>

      {/* Shared / advisor master list */}
      <motion.section variants={fadeInUp} className="space-y-4" data-testid="client-packing-shared">
        <EditorialRule label="Shared trip items" />
        {(data.shared?.categories || []).map((cat) => (
          <ClientCategoryBlock
            key={cat.id}
            category={cat}
            canCheck={canCheck}
            onToggle={toggleMaster}
            onHide={hideMaster}
          />
        ))}
        {(data.shared?.categories || []).length === 0 && (
          <p className="font-serif text-sm text-[hsl(var(--foreground-muted))]">
            Your advisor hasn't added any recommendations yet.
          </p>
        )}
      </motion.section>

      {/* Personal items */}
      {canEditPersonal && (
        <motion.section variants={fadeInUp} className="space-y-4" data-testid="client-packing-personal">
          <EditorialRule label="My personal list" />
          <p className="font-serif text-[13px] text-[hsl(var(--foreground-secondary))] -mt-1 max-w-prose">
            Private to you. Add anything else you'd like to remember to pack — nobody else on the trip can see this list.
          </p>
          <Surface variant="paper" padded={false}>
            {(data.personal?.items || []).map((it) => (
              <PersonalRow
                key={it.id}
                item={it}
                onToggle={(packed) => togglePersonal(it.id, packed)}
                onRename={(name) => renamePersonal(it.id, name)}
                onDelete={() => deletePersonal(it.id)}
              />
            ))}
            <AddPersonalRow onAdd={addPersonal} busy={busy} />
          </Surface>
        </motion.section>
      )}
    </motion.div>
  );
}

// ─── Sub-components ────────────────────────────────────────────────

function EmptyState({ advisorName }) {
  return (
    <motion.div
      initial="initial"
      animate="animate"
      variants={stagger}
      className="px-4 md:px-8 py-12 max-w-2xl"
      data-testid="client-packing-empty"
    >
      <motion.div variants={fadeInUp} className="flex flex-col items-start gap-4">
        <PackageOpen className="size-7 text-[hsl(var(--accent-gold))]" strokeWidth={1.25} />
        <h1 className="font-serif text-3xl text-[hsl(var(--foreground))]">
          Your packing list will arrive soon.
        </h1>
        <p className="font-serif text-[15px] text-[hsl(var(--foreground-secondary))] leading-relaxed max-w-prose">
          {advisorName} is finishing the final touches on your tailored list.
          You'll see it here the moment it's ready.
        </p>
      </motion.div>
    </motion.div>
  );
}

function ClientCategoryBlock({ category, canCheck, onToggle, onHide }) {
  const [expanded, setExpanded] = React.useState(false);
  const packed = (category.items || []).filter((i) => i.packed).length;
  const total = category.items?.length || 0;
  return (
    <Surface
      variant="paper"
      padded={false}
      data-testid={`client-packing-category-${category.id}`}
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between gap-3 px-5 py-3 text-left"
      >
        <div className="flex items-center gap-3">
          <ChevronDown
            className={`size-4 text-[hsl(var(--foreground-muted))] transition-transform ${
              expanded ? "" : "-rotate-90"
            }`}
            strokeWidth={1.5}
          />
          <span className="font-serif text-[15px] text-[hsl(var(--foreground))]">
            {category.name}
          </span>
        </div>
        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))]">
          {packed} / {total}
        </span>
      </button>
      {category.note && expanded && (
        <p className="px-5 -mt-1 pb-2 font-accent italic text-[12.5px] text-[hsl(var(--foreground-muted))]">
          {category.note}
        </p>
      )}
      {expanded && (
        <div className="divide-y divide-[hsl(var(--surface-hairline))]/20 border-t border-[hsl(var(--surface-hairline))]/30">
          {(category.items || []).map((it) => (
            <SharedRow
              key={it.id}
              item={it}
              canCheck={canCheck}
              onToggle={(v) => onToggle(it.id, v)}
              onHide={() => onHide(it.id)}
            />
          ))}
        </div>
      )}
    </Surface>
  );
}

function SharedRow({ item, canCheck, onToggle, onHide }) {
  const [noteOpen, setNoteOpen] = React.useState(false);
  const hasNote = !!(item.notes && item.notes.trim());
  return (
    <div className="flex items-start gap-3 px-5 py-3 group" data-testid={`client-shared-row-${item.id}`}>
      <button
        type="button"
        onClick={() => onToggle(!item.packed)}
        disabled={!canCheck}
        data-testid={`client-shared-check-${item.id}`}
        aria-label={item.packed ? "Mark unpacked" : "Mark packed"}
        className={`mt-0.5 size-5 rounded grid place-items-center transition-colors ${
          item.packed
            ? "bg-[hsl(var(--accent-gold))] text-white border border-[hsl(var(--accent-gold))]"
            : "border border-[hsl(var(--surface-hairline))]/60 hover:border-[hsl(var(--accent-gold))]/60"
        } ${!canCheck ? "opacity-60 cursor-not-allowed" : ""}`}
      >
        {item.packed ? <Check className="size-3" strokeWidth={2} /> : null}
      </button>
      <div className="flex-1 min-w-0">
        <p className={`font-serif text-sm ${
          item.packed
            ? "line-through text-[hsl(var(--foreground-muted))]"
            : "text-[hsl(var(--foreground))]"
        }`}>
          {item.quantity > 1 ? `${item.quantity}× ` : ""}{item.name}
        </p>
        {hasNote && !noteOpen && (
          <p className="font-accent italic text-[12px] text-[hsl(var(--foreground-muted))] mt-0.5 line-clamp-1">
            {item.notes}
          </p>
        )}
        {hasNote && noteOpen && (
          <p className="font-accent italic text-[12.5px] leading-relaxed text-[hsl(var(--foreground-secondary))] mt-1 border-l-2 border-[hsl(var(--accent-gold))]/40 pl-3" data-testid={`client-shared-note-${item.id}`}>
            {item.notes}
          </p>
        )}
        <div className="mt-1 flex items-center gap-3 flex-wrap">
          {hasNote && (
            <button
              type="button"
              onClick={() => setNoteOpen((v) => !v)}
              data-testid={`client-shared-note-toggle-${item.id}`}
              className="inline-flex items-center gap-1 font-mono text-[9.5px] uppercase tracking-[0.16em] text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--accent-gold))]"
            >
              {noteOpen ? "Hide note" : "View note"}
              <ChevronDown className={`size-3 transition-transform ${noteOpen ? "rotate-180" : ""}`} strokeWidth={1.5} />
            </button>
          )}
          {item.link && (
            <a
              href={item.link}
              target="_blank"
              rel="noreferrer noopener"
              data-testid={`client-shared-link-${item.id}`}
              className="inline-flex items-center gap-1 font-mono text-[9.5px] uppercase tracking-[0.16em] text-[hsl(var(--accent-gold))] hover:underline"
            >
              {item.link_label || "Open link"}
              <ExternalLink className="size-3" strokeWidth={1.5} />
            </a>
          )}
        </div>
      </div>
      <button
        type="button"
        onClick={onHide}
        data-testid={`client-shared-hide-${item.id}`}
        title="Remove from my list"
        aria-label="Remove from my list"
        className="opacity-0 group-hover:opacity-100 size-7 grid place-items-center text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))] transition-all"
      >
        <X className="size-3.5" strokeWidth={1.5} />
      </button>
    </div>
  );
}

function PersonalRow({ item, onToggle, onRename, onDelete }) {
  const [editing, setEditing] = React.useState(false);
  const [name, setName] = React.useState(item.name);
  const commit = () => {
    setEditing(false);
    const trimmed = name.trim();
    if (trimmed && trimmed !== item.name) onRename(trimmed);
  };
  return (
    <div className="flex items-center gap-3 px-5 py-3 group" data-testid={`client-personal-row-${item.id}`}>
      <button
        type="button"
        onClick={() => onToggle(!item.packed)}
        data-testid={`client-personal-check-${item.id}`}
        className={`size-5 rounded grid place-items-center transition-colors ${
          item.packed
            ? "bg-[hsl(var(--accent-gold))] text-white border border-[hsl(var(--accent-gold))]"
            : "border border-[hsl(var(--surface-hairline))]/60 hover:border-[hsl(var(--accent-gold))]/60"
        }`}
      >
        {item.packed ? <Check className="size-3" strokeWidth={2} /> : null}
      </button>
      <div className="flex-1 min-w-0">
        {editing ? (
          <input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === "Enter") commit();
              if (e.key === "Escape") { setName(item.name); setEditing(false); }
            }}
            className="w-full px-2 py-1 rounded border border-[hsl(var(--surface-hairline))]/50 bg-white font-serif text-sm"
          />
        ) : (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className={`text-left font-serif text-sm ${
              item.packed
                ? "line-through text-[hsl(var(--foreground-muted))]"
                : "text-[hsl(var(--foreground))]"
            }`}
          >
            {item.quantity > 1 ? `${item.quantity}× ` : ""}{item.name}
          </button>
        )}
      </div>
      <button
        type="button"
        onClick={onDelete}
        data-testid={`client-personal-delete-${item.id}`}
        className="opacity-0 group-hover:opacity-100 size-7 grid place-items-center text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--accent-terracotta,15_70%_55%))] transition-all"
        aria-label="Delete"
      >
        <Trash2 className="size-3.5" strokeWidth={1.5} />
      </button>
    </div>
  );
}

function AddPersonalRow({ onAdd, busy }) {
  const [name, setName] = React.useState("");
  const [qty, setQty] = React.useState(1);
  const submit = (ev) => {
    ev.preventDefault();
    if (!name.trim()) return;
    onAdd({ name: name.trim(), quantity: Number(qty) || 1 });
    setName("");
    setQty(1);
  };
  return (
    <form
      onSubmit={submit}
      className="flex items-center gap-2 px-5 py-3 border-t border-[hsl(var(--surface-hairline))]/30"
      data-testid="client-packing-personal-add"
    >
      <Plus className="size-3.5 text-[hsl(var(--foreground-muted))]" strokeWidth={1.5} />
      <input
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Add something to your list"
        data-testid="client-packing-personal-add-name"
        className="flex-1 px-2.5 py-1.5 rounded-md border border-[hsl(var(--surface-hairline))]/40 bg-white font-serif text-sm focus:outline-none focus:border-[hsl(var(--accent-gold))]/50"
      />
      <input
        type="number"
        min="1"
        value={qty}
        onChange={(e) => setQty(e.target.value)}
        className="w-16 px-2 py-1.5 rounded-md border border-[hsl(var(--surface-hairline))]/40 bg-white font-serif text-sm"
        aria-label="Quantity"
      />
      <button
        type="submit"
        disabled={busy}
        data-testid="client-packing-personal-add-submit"
        className="font-mono text-[10px] uppercase tracking-[0.18em] px-3 py-1.5 rounded-full bg-[hsl(var(--accent-gold))] text-white hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center gap-1.5"
      >
        <Sparkles className="size-3" strokeWidth={1.5} />
        Add
      </button>
    </form>
  );
}
